import type { ProjectSelectorVariant } from "@v2/features/dashboard/sidebar/project-selector/ProjectSelector";
import type {
  ReploProject,
  ReploSimpleProject,
  UserWorkspace,
} from "schemas/generated/project";

import * as React from "react";
import { useEffect, useState } from "react";

import { Badge } from "@replohq/lattice/components/badge/Badge";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@replohq/lattice/components/command/Command";
import { PopoverContent } from "@replohq/lattice/components/popover/Popover";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replohq/lattice/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replohq/lattice/hooks/use-overflow-detection";
import { cn } from "@replohq/lattice/utils/cn";
import LogoBadge from "@v2/components/LogoBadge";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import {
  Check,
  ChevronRight,
  PencilRuler,
  Plus,
  Repeat,
  Search,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { generatePath, useNavigate } from "react-router-dom";
import { setLastVisitedDashboardRoute } from "replo-utils/lib/lastVisitedDashboardIds";
import { getProjectSelectorSearchGroups } from "replo-utils/lib/projectSelectorSearch";

const SEARCH_GROUP_HEADING_CLASS = "**:[[cmdk-group-heading]]:text-xs!";

function getProjectNavigationPath({
  project,
  hostingType,
}: {
  project: {
    id: string;
    agentEnabledAt?: Date | null;
  };
  hostingType: "replo" | "shopify";
}): string {
  if (hostingType !== "replo") {
    return generatePath(routes.shops.site, { projectId: project.id });
  }
  const homeRoute = project.agentEnabledAt
    ? routes.agent.root
    : routes.shops.home;
  return generatePath(homeRoute, { projectId: project.id });
}

const ProjectMenuItem = ({
  project,
  isCurrentProject,
  onSelect,
  isMobile,
}: {
  project: ReploSimpleProject;
  isCurrentProject: boolean;
  onSelect: () => void;
  isMobile: boolean;
}) => {
  const isV2Project = project.hostingType === "replo";
  const isAgentProject = isV2Project && Boolean(project.agentEnabledAt);

  return (
    <CommandItem
      value={`${getProjectName(project)}-${project.id}`}
      size="base"
      UNSAFE_className={isMobile ? "min-h-11" : undefined}
      startEnhancer={
        <Badge
          size={isMobile ? "lg" : "base"}
          className={
            isV2Project
              ? cn(
                  "[&>svg]:size-full!",
                  !isAgentProject && "grayscale opacity-45",
                )
              : undefined
          }
        >
          {isV2Project ? (
            <LogoBadge />
          ) : (
            <PencilRuler className="w-full h-full" />
          )}
        </Badge>
      }
      label={getProjectName(project)}
      selected={isCurrentProject}
      endEnhancer={isCurrentProject ? <Check /> : null}
      onSelect={onSelect}
    />
  );
};

const WorkspaceCommandItem = ({
  workspace,
  onSelect,
}: {
  workspace: UserWorkspace;
  onSelect: () => void;
}) => {
  return (
    <CommandItem
      value={`${workspace.workspaceName}-${workspace.workspaceId}`}
      size="lg"
      label={workspace.workspaceName}
      description={`${workspace.projects.length} ${workspace.projects.length !== 1 ? "projects" : "project"}`}
      endEnhancer={workspace.projects.length > 1 ? <ChevronRight /> : null}
      onSelect={onSelect}
      UNSAFE_className="items-center"
    />
  );
};

function selectWorkspaceOrOnlyProject({
  workspace,
  onSelectWorkspace,
  onSelectProject,
}: {
  workspace: UserWorkspace;
  onSelectWorkspace: (workspaceId: string) => void;
  onSelectProject: (project: ReploSimpleProject) => void;
}) {
  if (workspace.projects.length <= 1 && workspace.projects[0]) {
    onSelectProject(workspace.projects[0]);
    return;
  }
  onSelectWorkspace(workspace.workspaceId);
}

const ProjectMenu = ({
  selectedWorkspace,
  workspaces,
  setSelectedWorkspaceId,
  setIsCreateShopModalOpen,
  setCreateShopWorkspaceId,
  currentProject,
  setIsOpen,
  isMobile,
}: {
  selectedWorkspace?: UserWorkspace;
  workspaces?: UserWorkspace[];
  setSelectedWorkspaceId: (workspaceId: string) => void;
  setIsCreateShopModalOpen: (isCreateShopModalOpen: boolean) => void;
  setCreateShopWorkspaceId: (workspaceId: string | undefined) => void;
  currentProject?: ReploProject;
  setIsOpen: (open: boolean) => void;
  isMobile: boolean;
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectNameOverflowRef, isProjectNameOverflowing] =
    useOverflowDetection();
  const [workspaceNameOverflowRef, isWorkspaceNameOverflowing] =
    useOverflowDetection();

  const onProjectClick = (project: ReploSimpleProject) => {
    // Write before navigate so a quick jump to "/" still sees this project.
    setLastVisitedDashboardRoute(project);
    navigate(
      getProjectNavigationPath({
        project,
        hostingType: project.hostingType,
      }),
    );

    logEvent("dashboard.shop.select", {
      toShopName: project.name ?? "",
      toShopId: project.id,
    });

    setIsOpen(false);
  };

  const sortedProjects = React.useMemo(() => {
    if (!selectedWorkspace?.projects) {
      return undefined;
    }

    const projects = [...selectedWorkspace.projects].sort((first, second) => {
      return getProjectName(first).localeCompare(
        getProjectName(second),
        undefined,
        { sensitivity: "base" },
      );
    });

    if (!currentProject) {
      return projects;
    }

    // NOTE (Ben, 2026-02-23): Move the current project to the front.
    const current = projects.find((project) => {
      return project.id === currentProject.id;
    });

    return current
      ? [
          current,
          ...projects.filter((project) => project.id !== currentProject.id),
        ]
      : projects;
  }, [selectedWorkspace?.projects, currentProject]);

  const isSearching = Boolean(searchQuery.trim());
  const {
    matchingWorkspaces,
    currentWorkspaceProjectGroup,
    otherProjectGroups,
  } = React.useMemo(() => {
    return getProjectSelectorSearchGroups({
      workspaces: workspaces ?? [],
      query: searchQuery,
      getProjectName: (project) => getProjectName(project),
      currentWorkspaceId: selectedWorkspace?.workspaceId,
    });
  }, [workspaces, searchQuery, selectedWorkspace?.workspaceId]);

  return (
    <>
      <div
        className={cn(
          "flex flex-col border-b-0.5 border-border",
          isMobile ? "gap-5 px-5 pb-5" : "gap-2 p-3",
        )}
      >
        <div className="flex flex-col">
          <TooltipRoot
            disabled={!isProjectNameOverflowing || !currentProject}
            disableHoverablePopup
          >
            <TooltipTrigger
              className="min-w-0 w-full"
              UNSAFE_className="block text-left"
            >
              <span
                ref={projectNameOverflowRef}
                className="header-h6 line-clamp-1 truncate min-w-0 block"
              >
                {currentProject ? (
                  getProjectName(currentProject)
                ) : (
                  <Skeleton className="w-20 h-4" />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">
              {currentProject ? getProjectName(currentProject) : undefined}
            </TooltipContent>
          </TooltipRoot>

          <TooltipRoot
            disabled={!isWorkspaceNameOverflowing || !selectedWorkspace}
            disableHoverablePopup
          >
            <TooltipTrigger
              className="min-w-0 w-full"
              UNSAFE_className="block text-left"
            >
              <span
                ref={workspaceNameOverflowRef}
                className="text-sm text-muted line-clamp-1 truncate min-w-0 block"
              >
                {selectedWorkspace ? (
                  selectedWorkspace.workspaceName
                ) : (
                  <Skeleton className="w-20 h-3" />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">
              {selectedWorkspace ? selectedWorkspace.workspaceName : undefined}
            </TooltipContent>
          </TooltipRoot>
        </div>
      </div>

      <Command shouldFilter={false} UNSAFE_className="p-0 rounded-none gap-0">
        <CommandInput
          placeholder="Search workspaces and projects..."
          startEnhancer={<Search />}
          UNSAFE_className={isMobile ? "mx-5 my-5 w-auto" : "m-2 mb-0 w-auto"}
          autoFocus
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty />
          {isSearching ? (
            <>
              {currentWorkspaceProjectGroup && (
                <CommandGroup
                  heading={currentWorkspaceProjectGroup.workspace.workspaceName}
                  UNSAFE_className={cn(
                    isMobile ? "px-5 pb-5" : "p-2",
                    SEARCH_GROUP_HEADING_CLASS,
                  )}
                >
                  {currentWorkspaceProjectGroup.projects.map((project) => (
                    <ProjectMenuItem
                      key={project.id}
                      project={project}
                      isCurrentProject={project.id === currentProject?.id}
                      onSelect={() => onProjectClick(project)}
                      isMobile={isMobile}
                    />
                  ))}
                </CommandGroup>
              )}
              {otherProjectGroups.map(({ workspace, projects }) => (
                <CommandGroup
                  key={workspace.workspaceId}
                  heading={workspace.workspaceName}
                  UNSAFE_className={cn(
                    isMobile ? "px-5 pb-5" : "p-2",
                    SEARCH_GROUP_HEADING_CLASS,
                  )}
                >
                  {projects.map((project) => (
                    <ProjectMenuItem
                      key={project.id}
                      project={project}
                      isCurrentProject={project.id === currentProject?.id}
                      onSelect={() => onProjectClick(project)}
                      isMobile={isMobile}
                    />
                  ))}
                </CommandGroup>
              ))}
              {matchingWorkspaces.length > 0 && (
                <CommandGroup
                  heading="Workspaces"
                  UNSAFE_className={cn(
                    isMobile ? "px-5 pb-5" : "p-2",
                    SEARCH_GROUP_HEADING_CLASS,
                  )}
                >
                  {matchingWorkspaces.map((workspace) => (
                    <WorkspaceCommandItem
                      key={workspace.workspaceId}
                      workspace={workspace}
                      onSelect={() => {
                        selectWorkspaceOrOnlyProject({
                          workspace,
                          onSelectWorkspace: (workspaceId) => {
                            setSearchQuery("");
                            setSelectedWorkspaceId(workspaceId);
                          },
                          onSelectProject: onProjectClick,
                        });
                      }}
                    />
                  ))}
                </CommandGroup>
              )}
            </>
          ) : (
            <CommandGroup UNSAFE_className={isMobile ? "px-5 pb-5" : "p-2"}>
              <div
                className={cn("flex flex-col", isMobile ? "gap-1" : "gap-0.5")}
              >
                <CommandItem
                  value="new project"
                  size="base"
                  UNSAFE_className={isMobile ? "min-h-11" : undefined}
                  startEnhancer={
                    <Badge
                      size={isMobile ? "lg" : "base"}
                      className="border-dashed border-1 bg-sheet"
                    >
                      <Plus />
                    </Badge>
                  }
                  label="New Project"
                  onSelect={() => {
                    setCreateShopWorkspaceId(selectedWorkspace?.workspaceId);
                    setIsCreateShopModalOpen(true);
                    setIsOpen(false);
                  }}
                  data-testid={`new-shop-${selectedWorkspace?.workspaceId}`}
                />

                {sortedProjects
                  ? sortedProjects.map((project) => (
                      <ProjectMenuItem
                        key={project.id}
                        project={project}
                        isCurrentProject={project.id === currentProject?.id}
                        onSelect={() => onProjectClick(project)}
                        isMobile={isMobile}
                      />
                    ))
                  : Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} className="h-8 w-full" />
                    ))}
              </div>
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </>
  );
};

const WorkspaceMenu = ({
  workspaces,
  currentWorkspaceId,
  currentProject,
  setSelectedWorkspaceId,
  setIsOpen,
  isMobile,
}: {
  workspaces?: UserWorkspace[];
  currentWorkspaceId: string;
  currentProject?: ReploProject;
  setSelectedWorkspaceId: (workspaceId: string) => void;
  setIsOpen: (open: boolean) => void;
  isMobile: boolean;
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const [searchQuery, setSearchQuery] = useState("");

  const onProjectClick = (project: ReploSimpleProject) => {
    setLastVisitedDashboardRoute(project);
    navigate(
      getProjectNavigationPath({
        project,
        hostingType: project.hostingType,
      }),
    );

    logEvent("dashboard.shop.select", {
      toShopName: project.name ?? "",
      toShopId: project.id,
    });

    setIsOpen(false);
  };

  const sortedWorkspaces = React.useMemo(() => {
    if (!workspaces) {
      return undefined;
    }

    if (!currentWorkspaceId) {
      return workspaces;
    }

    // NOTE (Ben, 2026-02-23): Move the current workspace to the front.
    const workspacesList = [...workspaces];
    const current = workspacesList.find(
      (workspace) => workspace.workspaceId === currentWorkspaceId,
    );

    return current
      ? [
          current,
          ...workspacesList.filter(
            (workspace) => workspace.workspaceId !== currentWorkspaceId,
          ),
        ]
      : workspacesList;
  }, [workspaces, currentWorkspaceId]);

  const isSearching = Boolean(searchQuery.trim());
  const { matchingWorkspaces, otherProjectGroups } = React.useMemo(() => {
    return getProjectSelectorSearchGroups({
      workspaces: sortedWorkspaces ?? [],
      query: searchQuery,
      getProjectName: (project) => getProjectName(project),
    });
  }, [sortedWorkspaces, searchQuery]);

  return (
    <>
      <Command shouldFilter={false} UNSAFE_className="p-0 rounded-none gap-0">
        <CommandInput
          placeholder="Search workspaces and projects..."
          startEnhancer={<Search />}
          UNSAFE_className={isMobile ? "mx-5 my-5 w-auto" : "m-2 mb-0 w-auto"}
          autoFocus
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty />
          {sortedWorkspaces ? (
            <>
              {matchingWorkspaces.length > 0 && (
                <CommandGroup
                  heading={isSearching ? "Workspaces" : undefined}
                  UNSAFE_className={cn(
                    isMobile ? "px-5 pb-5 gap-1" : "p-2",
                    isSearching && SEARCH_GROUP_HEADING_CLASS,
                  )}
                >
                  {matchingWorkspaces.map((workspace) => (
                    <WorkspaceCommandItem
                      key={workspace.workspaceId}
                      workspace={workspace}
                      onSelect={() => {
                        selectWorkspaceOrOnlyProject({
                          workspace,
                          onSelectWorkspace: (workspaceId) => {
                            setSearchQuery("");
                            setSelectedWorkspaceId(workspaceId);
                          },
                          onSelectProject: onProjectClick,
                        });
                      }}
                    />
                  ))}
                </CommandGroup>
              )}
              {otherProjectGroups.map(({ workspace, projects }) => (
                <CommandGroup
                  key={workspace.workspaceId}
                  heading={workspace.workspaceName}
                  UNSAFE_className={cn(
                    isMobile ? "px-5 pb-5" : "p-2",
                    SEARCH_GROUP_HEADING_CLASS,
                  )}
                >
                  {projects.map((project) => (
                    <ProjectMenuItem
                      key={project.id}
                      project={project}
                      isCurrentProject={project.id === currentProject?.id}
                      onSelect={() => onProjectClick(project)}
                      isMobile={isMobile}
                    />
                  ))}
                </CommandGroup>
              ))}
            </>
          ) : (
            <CommandGroup
              UNSAFE_className={isMobile ? "px-5 pb-5 gap-1" : "p-2"}
            >
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </>
  );
};

const SLIDE_OFFSET = 16;
const SLIDE_DURATION = 0.1;

const slideVariants = {
  enter: (direction: number) => ({ x: direction * SLIDE_OFFSET, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction * -SLIDE_OFFSET, opacity: 0 }),
};

const ProjectSelectorMenuContent = ({
  currentWorkspaceId,
  workspaces,
  setIsCreateShopModalOpen,
  setCreateShopWorkspaceId,
  setIsAddWorkspaceModalOpen,
  currentProject,
  isOpen,
  setIsOpen,
  variant,
  isMobile,
}: {
  currentWorkspaceId: string;
  workspaces?: UserWorkspace[];
  setIsCreateShopModalOpen: (isCreateShopModalOpen: boolean) => void;
  setCreateShopWorkspaceId: (workspaceId: string | undefined) => void;
  setIsAddWorkspaceModalOpen: (isAddWorkspaceModalOpen: boolean) => void;
  currentProject?: ReploProject;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  variant: ProjectSelectorVariant;
  isMobile: boolean;
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    currentWorkspaceId,
  );
  const slideDirectionRef = React.useRef<1 | -1>(1);
  const isAgentBrowser = variant === "agent-browser";

  // NOTE (Ben, 2026-02-19): Reset the selected workspace when the popover is closed
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  useEffect(() => {
    if (!isOpen) {
      setSelectedWorkspaceId(currentWorkspaceId);
      slideDirectionRef.current = 1;
    }
  }, [isOpen, currentWorkspaceId]);

  const selectedWorkspace = workspaces?.find(
    (workspace) => workspace.workspaceId === selectedWorkspaceId,
  );

  const handleSwitchWorkspace = () => {
    slideDirectionRef.current = -1;
    setSelectedWorkspaceId(null);
  };

  const handleSelectWorkspace = (workspaceId: string) => {
    slideDirectionRef.current = 1;
    setSelectedWorkspaceId(workspaceId);
  };

  const handleNewWorkspace = () => {
    setIsAddWorkspaceModalOpen(true);
    setIsOpen(false);
  };

  const menuBody = (
    <>
      <AnimatePresence
        mode="wait"
        custom={slideDirectionRef.current}
        initial={false}
      >
        {selectedWorkspaceId ? (
          <motion.div
            key="project-menu"
            className="min-h-0 flex flex-col overflow-hidden"
            custom={slideDirectionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: SLIDE_DURATION, ease: "easeOut" }}
          >
            <ProjectMenu
              currentProject={currentProject}
              selectedWorkspace={selectedWorkspace}
              workspaces={workspaces}
              setSelectedWorkspaceId={handleSelectWorkspace}
              setIsCreateShopModalOpen={setIsCreateShopModalOpen}
              setCreateShopWorkspaceId={setCreateShopWorkspaceId}
              setIsOpen={setIsOpen}
              isMobile={isMobile}
            />
          </motion.div>
        ) : (
          <motion.div
            key="workspace-menu"
            className="min-h-0 flex flex-col overflow-hidden"
            custom={slideDirectionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: SLIDE_DURATION, ease: "easeOut" }}
          >
            <WorkspaceMenu
              workspaces={workspaces}
              currentWorkspaceId={currentWorkspaceId}
              currentProject={currentProject}
              setSelectedWorkspaceId={handleSelectWorkspace}
              setIsOpen={setIsOpen}
              isMobile={isMobile}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={cn(
          "sticky bottom-0 border-t-0.5 border-border bg-sheet",
          isMobile ? "p-5" : "px-5 py-2",
        )}
      >
        <Button
          variant={isMobile ? "secondary" : "link"}
          size={isMobile ? "lg" : "sm"}
          UNSAFE_className={isMobile ? "w-full" : undefined}
          startEnhancer={selectedWorkspaceId ? <Repeat /> : <Plus />}
          onClick={
            selectedWorkspaceId ? handleSwitchWorkspace : handleNewWorkspace
          }
        >
          {selectedWorkspaceId ? "Switch Workspace" : "New Workspace"}
        </Button>
      </div>
    </>
  );

  // NOTE (Noah, 2026-07-13, REPL-29216): On mobile the caller wraps this body in
  // a bottom-sheet Drawer. The Drawer must live outside the Popover (nesting it
  // inside made the Popover's outside-press dismissal close the sheet on any tap
  // and steal focus from the search input), so this component only renders the
  // shared body and lets the caller own the shell.
  if (isMobile) {
    return (
      <div className="-mx-5 -mb-5 flex flex-col overflow-hidden">
        {menuBody}
      </div>
    );
  }

  return (
    <>
      {isOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[99]"
          onPointerDown={() => setIsOpen(false)}
        />
      )}
      <PopoverContent
        align={isAgentBrowser ? "end" : "start"}
        alignOffset={isAgentBrowser ? 0 : -12}
        side={isAgentBrowser ? "bottom" : "right"}
        sideOffset={isAgentBrowser ? 0 : 16}
        showCloseButton={false}
        UNSAFE_className="p-0 gap-0 overflow-hidden max-h-[min(--spacing(150),var(--available-height))] w-62"
      >
        {menuBody}
      </PopoverContent>
    </>
  );
};

export default ProjectSelectorMenuContent;
